import once from 'lodash/once';
import debounce from 'lodash/debounce';
import dropdown from '../dropdown.js';

/**
 * Generic slider component. The associated Mustache template at serverside is named 'tpl-v10-mustache-generic-slider'.
 * Underlying element is a native HTML5 input of type range. It's set opaque, and under it is drawn a custom slider
 * track and slider knob. The value of the slider is written inside the button. You can customize the button / value
 * display and using the class "target" decide where specifically the value should be updated. The component has a
 * hidden value field which will act as programmable interface. You can name the field as one of the parameters for
 * the mustache associated with this component.
 *
 * TO-DO: Test browser compatibility. Only Mac Chrome tested.
 *
 * Please avoid jQuery here if possible. Vanilla JS does wonders.
 *
 * Adding event listener to listen event is now possible, this way is possible to get actual value of selection not just
 * index number of the selection. :)
 * @example
 * document.querySelector('generic-slider').addEventListener('slider-change', (event) => {
 *   console.log( event.detail.labelValue, event.detail.value );
 * });
 *
 * @type {Object}
 * @author KL
 */

const sliderComponent = {
  init: once(() => {
    // Find all sliders on page and init them
    document.querySelectorAll('.generic-slider').forEach(sliderComponent.initSlider);
    // Find all sliders on page and attach a respawn listener to them.
    document.querySelectorAll('.generic-slider,.generic-slider-container').forEach(sliderComponent.reSpawn);

    //Rerender debounced is fired only every two seconds if a window is resized
    const reRenderDebounced = debounce(
      () => {
        document.querySelectorAll('.generic-slider').forEach(sliderComponent.initSlider);
      },
      2000,
      { trailing: true, leading: false },
    );

    window.addEventListener(
      'resize',
      function(event) {
        reRenderDebounced();
      },
      true,
    );
  }),
  /**
   * If the element is re-rendered, this will initialize the functionality again. It detects if and when the
   * child element is redrawn and tries to find again the slider component and initializes it again to restore
   * functionality. This allows the developer to simply re-render the Mustache with new values at any time,
   * and it will keep on working.
   *
   * @param  {object} element HTML node element of the compnents parent.
   */
  reSpawn: element => {
    const observer = new MutationObserver(mutationRecord => {
      mutationRecord[0].target.querySelectorAll('.generic-slider').forEach(sliderComponent.initSlider);
    });
    observer.observe(element.parentNode, { attributes: false, childList: true });
  },

  /**
   * Calculate track bar height and position
   * @param {HTMLElement} display display element
   * @param {HTMLElement} slider slider element
   * @param {HTMLElement} track track element
   */
  calculateHeights: (display, slider, track) => {
    slider.style.height = `${display.offsetHeight}px`;
  },

  /**
   * Initialize slider
   * @param {HTMLElement} element Slider's HTMLElement
   */
  initSlider: element => {
    const display = element.querySelector('.generic-slider-display');
    const tooltip = element.querySelector('.generic-slider-tooltip');
    const slider = element.querySelector('input[type="range"]');
    const select = element.querySelector('select');
    const track = element.querySelector('.generic-slider-track');

    sliderComponent.calculateHeights(display, slider, track);

    const trackColorOriginal = track.getAttribute('trackColorOriginal');
    const trackColor = trackColorOriginal || window.getComputedStyle(track).backgroundColor || 'rgba(128,128,128,0.5)';

    let labels = [];

    try {
      labels = [...JSON.parse(element.getAttribute('data-labels-json'))];
    } catch (exception) {
      console.log('could not parse data-labels-json');
    }

    display.data = {
      scale: (slider.max || 10) - (slider.min || 0),
      labels,
      track,
      trackColor,
      field: select,
      slider: slider,
      target: element.querySelector('.generic-slider-display .target') ?? element.parentElement.querySelector('.generic-slider-header .target'),
    };

    display.setValue = function(value) {
      const { scale, labels, field, target, track, trackColor, slider } = this.data;
      if(!labels[value]) return;

      if (tooltip) {
        tooltip.dataset.value = labels[value].label || value;
      }

      // 1. update the display unit to new label value
      if (target && element.dataset.displaytext === 'true') {
        target.innerText = `${labels[value].label || value}`;
      } else if (element.dataset.displaytext === 'true') {
        this.innerText = `${labels[value].label || value}`;
      }

      //Update dataset value is more easely accessible
      element.dataset.currentLabel = labels[value].label;
      element.dataset.currentValue = labels[value].key;

      const sliderWidth = this.offsetWidth;

      // 2. update the slider & select alternative (if not already set)

      const selectedOption = field.querySelector('[selected="selected"]');
      const option = field.querySelector(`[value="${labels[value].key}"]`);

      if (option && !option.getAttribute('selected')) {
        if (selectedOption) {
          selectedOption.removeAttribute('selected');
        }

        option.setAttribute('selected', 'selected');
      }

      if (slider.value !== value) {
        slider.value = value;
      }

      // 3. move the display unit & track background to new position
      const percentage = `${(value / scale) * 100}%`;
      this.style.left = `calc(${percentage} - ${(value / scale) * sliderWidth}px)`;
      track.setAttribute('trackColorOriginal', trackColor);
      track.style.background = `linear-gradient(90deg, ${trackColor} ${percentage}, #f3f3f3 ${percentage})`;

      //Dispatch custom event
      element.dispatchEvent(
        new CustomEvent('slider-change', {
          bubbles: true,
          detail: { labelValue: labels[value].label, inputValue: value },
        }),
      );
    };

    const initialValue = select.getAttribute('data-default') || '';
    const sliderValue = labels.findIndex(pair => String(pair.key) === initialValue);

    display.setValue(Math.max(0, sliderValue));

    slider.display = display;

    slider.addEventListener('input', function(event) {
      this.display.setValue(this.value);
    });

    select.display = display;

    select.addEventListener('change', function(event) {
      this.display.setValue(this.selectedIndex);
    });

    dropdown.create({ custom: select }, null);
  },
};

export default sliderComponent;
